import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY_PREFIX = 'payzen_env_';

const EnvironmentContext = createContext(null);

export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within EnvironmentProvider');
  }
  return context;
};

function getStorageKey(userId) {
  return userId ? `${STORAGE_KEY_PREFIX}${userId}` : 'payzen_env_guest';
}

export const EnvironmentProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [isLive, setIsLiveState] = useState(() => {
    try {
      return localStorage.getItem('payzen_env_guest') === 'live';
    } catch {
      return false;
    }
  });

  // When user changes (login/logout), load that user's last environment preference.
  // Only sync payzen_env_current when we have a userId so env switch + reload keeps Live (no overwrite with guest).
  useEffect(() => {
    try {
      const key = getStorageKey(userId);
      const stored = localStorage.getItem(key);
      const isLiveVal = stored === 'live';
      setIsLiveState(isLiveVal);
      if (userId) localStorage.setItem('payzen_env_current', isLiveVal ? 'live' : 'sandbox');
    } catch {
      setIsLiveState(false);
      try {
        if (userId) localStorage.setItem('payzen_env_current', 'sandbox');
      } catch {
        // ignore
      }
    }
  }, [userId]);

  // Persist current user's choice when it changes. Only sync payzen_env_current when we have a user
  // so that on reload (e.g. after env switch) we don't overwrite it with guest/sandbox before user loads.
  useEffect(() => {
    try {
      const value = isLive ? 'live' : 'sandbox';
      const key = getStorageKey(userId);
      localStorage.setItem(key, value);
      if (userId) localStorage.setItem('payzen_env_current', value);
    } catch {
      // ignore
    }
  }, [userId, isLive]);

  const setIsLive = (value) => {
    setIsLiveState(Boolean(value));
  };

  const toggleEnvironment = () => {
    setIsLiveState((prev) => !prev);
  };

  const value = {
    isLive,
    isSandbox: !isLive,
    setIsLive,
    toggleEnvironment,
  };

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};
